import { useMemo, useCallback, useEffect } from "react"
import {
    APIAppTemplateModel,
    getInputSchema,
    nameToSlug
} from "../../../../../../../Models/API/AppTemplateModel"
import { Title, Text, OnboardingScreenContainer } from "dash-styled"
import {
    Render,
    InputRenderData
} from "../../../../../../Common/Render/Variants/JobInput/Common/Render/Render"
import { InputMode } from "../../../../../../Common/Render/Variants/JobInput/Common/Container/Container"
import { OnboardingProgress } from "../../../../Common/OnboardingProgress/OnboardingProgress"
import { TemplateInstantiateStage } from "../../../../../../../Models/Client/TemplateInstantiation"
import { useDispatch } from "react-redux"
import { useAppState } from "../../../../../../../Hooks/useReduxState"
import {
    changeInputValue,
    clearInputsForId
} from "../../../../../../../Actions/Client/InputRender/InputRenderActions"
import { useBoolean } from "../../../../../../../Hooks/useBoolean"
import { canSubmit } from "../../../../../../../Reducers/Client/InputRender/InputRenderReducer"

export type NoSDKInput = Exclude<InputMode, "sdk">

interface CreateJobStageProps {
    orgUsername: string
    template: APIAppTemplateModel
    formID: string
    mode: InputMode
    onBack: () => void
    onForward: () => void
    changeMode: (button: NoSDKInput) => void
    back: boolean
}

export const CreateJobStage = ({
    orgUsername,
    template,
    formID,
    onBack,
    onForward,
    changeMode,
    mode,
    back
}: CreateJobStageProps) => {
    const inputSchema = useMemo(
        () => getInputSchema(template, false),
        [template]
    )

    const handleChangeMode = useCallback(
        (button: InputMode) => {
            if (button === "sdk") return
            changeMode(button)
        },
        [changeMode]
    )

    const formData = useAppState(
        state => state.client.inputRender.entries[formID]
    )

    const [initiateSubmit, startSubmit, stopSubmit] = useBoolean(false)

    const dispatch = useDispatch()

    const handleInputChange = useCallback(
        (data: InputRenderData) => {
            dispatch(
                changeInputValue(formID, { ...data, surviveReroute: true })
            )
            if (data.canSubmit) {
                onForward?.()
            }
        },
        [formID, dispatch, onForward]
    )

    const handleNonInputModeSubmit = canSubmit(formData)
        ? () => onForward?.()
        : undefined

    useEffect(() => {
        // start with clean slate
        if (mode !== "json") dispatch(clearInputsForId(formID))
    }, [formID, dispatch, mode])

    return (
        <OnboardingScreenContainer
            celebrate={false}
            forward={true}
            onForward={
                mode === "input" ? startSubmit : handleNonInputModeSubmit
            }
        >
            <OnboardingProgress
                currentStage={TemplateInstantiateStage.createJob}
                back={back}
                onBack={onBack}
            />
            <div
                id="maincontent"
                className="flex flex-col px-4 w-full md:w-3/4 lg:w-2/3 xl:w-1/2 pb-10"
                data-testid="onboarding-start"
            >
                <Title color="darkviolet" variant="heading1" weight="semibold">
                    Add some data to try us out
                </Title>
                <Text color="gray">
                    Start with one data point — you can add more once you're
                    happy
                </Text>
                <div className="mt-3">
                    <Render
                        {...inputSchema}
                        orgUsername={orgUsername}
                        appID={undefined}
                        testId="onboarding-job-creation-form"
                        id={formID}
                        onChangeMode={handleChangeMode}
                        activeMode={mode}
                        single={true}
                        contextName={nameToSlug(template.name)}
                        uploaderStateKey={`${formID}-instance-input-upload`}
                        onInputChange={handleInputChange}
                        inputs={formData}
                        initiateSubmit={initiateSubmit}
                        stopSubmit={stopSubmit}
                    />
                </div>
            </div>
        </OnboardingScreenContainer>
    )
}
